import { AiCommand, AiRjsfFormDescriptor } from '../aiLayer';
import { ChatContent } from '../types';
import { BaseEntity } from '../types/baseEntity';

import { FilePayload } from './filePayload';

// Points at an uploaded file without naming anything the caller could ask S3 for.
// The server resolves it inside the org that owns the conversation, and only after
// confirming that conversation really did cite it.
export type ChatMessageAiAnswerSourceFileRef = {
  /** `uploads_<datasourceId>` — the sync collection holding the indexed record. */
  collection: string;
  /** The indexed record's id (the `dbId` carried in Pinecone metadata). */
  documentId: string;
};

export type ChatMessageAiAnswerSource = {
  id?: number | string;
  description?: string;
  /** Empty for uploaded files — they have no public URL. See `fileRef`. */
  url: string;
  title: string;
  type: string;
  fileRef?: ChatMessageAiAnswerSourceFileRef;
};

export type ChatMessageAiCitation = {
  paragraph: string;
  citations: { sourceIdx: number; citation: string }[];
};

export type AiMessageReply = {
  message: string;
  sources: ChatMessageAiAnswerSource[];
  followups?: string[];
  citations?: ChatMessageAiCitation[];
  form?: AiRjsfFormDescriptor;
  commands?: AiCommand[];
  relevantKnowledgeNotFound?: boolean;
  attachments?: FilePayload[];
};

export type AiMessageContent = AiMessageReply | { error: string };

// What an end user sees when the AI step fails. Upstream error text (OpenAI, LangChain,
// the ai layer) is logged, never shown — it reaches widgets and Slack handoff threads.
export const AI_ERROR_FALLBACK_MESSAGE =
  "Sorry, I couldn't answer that right now. Please try again in a moment.";

interface BaseChatMessage extends BaseEntity {
  chatId: string;
  attachments?: string[];
}

export interface HumanChatMessage extends BaseChatMessage {
  role: 'human';
  content: ChatContent;
  userEmail: string;
  sourcePlatformData: { platform: 'slack'; threadTs: string };
  display?: string;
}

export interface UserToAiChatMessage extends BaseChatMessage {
  role: 'user';
  to: 'ai';
  actionId: string;
  content: ChatContent;
  display?: string;
  isAutoMessage?: boolean;
}

export interface UserToHumanChatMessage extends BaseChatMessage {
  role: 'user';
  to: 'human';
  content: ChatContent;
  display?: string;
}

export interface AiChatMessage extends BaseChatMessage {
  role: 'ai';
  replyToMessageId: string;
  content: AiMessageContent;
}

export interface SystemChatMessage extends BaseChatMessage {
  role: 'system';
  content: ChatContent;
  hidden?: boolean;
}

export type ChatMessage =
  | HumanChatMessage
  | UserToAiChatMessage
  | UserToHumanChatMessage
  | AiChatMessage
  | SystemChatMessage;
